import * as React from 'react';
import { useEffect } from 'react';
import { ListItem as MuiListItem, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
    IoMenuOutline,
    IoChevronForwardCircleOutline,
    IoChevronBackCircleOutline,
    IoCloseOutline,
} from "react-icons/io5";
import { AppBar, Drawer, DrawerHeader, PopupProvider, TextOverflow, getCenterStyles } from "vig-ui-library";
import { useNavigate, useMatch } from "react-router-dom";
import { Route, useRouter, routes } from '../Router/router';
import { useAppProvider } from '@/Provider/AppProvider';
import { IoLogOut } from "react-icons/io5";
import { LogoutHandler } from '@/Components/LogoutHandler';
import { companyType, getCompanyLogoUrl, getCompanyLogoAlt } from '@/Utils/environment';
import { CompanyType } from '@/Enums/CompanyType';
import { getAssemblyVersion } from '@/Api/VersionApi';

export type BaseLayout = {
    children: React.ReactNode;
}

export default function BaseLayout({ children }: BaseLayout) {
    const { currentUsername } = useAppProvider();

    const theme = useTheme();

    const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [open, setOpen] = React.useState(!onlyMediumScreen);

    const [assemblyVersion, setAssemblyVersion] = React.useState<string | null>(null);

    useEffect(() => {
        getAssemblyVersion()
            .then((version) => setAssemblyVersion(version))
            .catch(() => { });
    }, []);

    const { currentRoute } = useRouter();

    const getContentWidth = (): string => {
        if (onlyMediumScreen) return "100vw";
        if (open) return `calc(100vw - ${theme.variables.drawerWidth}px)`;
        return `calc(100vw - 65px)`;
    }

    const { permissions } = useAppProvider();

    return (
        <Box sx={{ display: 'flex' }} height="100vh" overflow="hidden">
            <AppBar drawerOpen={open}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="Rozbaliť bočný panel"
                        onClick={() => setOpen(true)}
                        edge="start"
                        sx={{
                            marginRight: onlySmallScreen ? 1 : 5,
                            ...(open && !onlyMediumScreen && { display: 'none' }),
                        }}
                    >
                        <IoMenuOutline />
                    </IconButton>

                    <Box sx={{ flexGrow: 1 }}>
                        {currentRoute?.name ? (
                            <Typography variant="h6" component="h1" noWrap>
                                {currentRoute.name}
                            </Typography>
                        ) : null}
                    </Box>

                    <Box sx={{ display: "flex", gap: theme.spacing(2), ...getCenterStyles() }}>
                        <Typography variant="body2">{currentUsername}</Typography>
                        <LogoutHandler>
                            <IconButton type="submit" color="primary" aria-label="Odhlásiť sa">
                                <IoLogOut />
                            </IconButton>
                        </LogoutHandler>
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer open={open} onClose={() => setOpen(false)}>
                <DrawerHeader>
                    {open && <div style={{ left: theme.spacing(1), width: "100%" }}>
                        <img src={getCompanyLogoUrl()} alt={getCompanyLogoAlt()} style={{ height: "100%", width: "auto", maxHeight: 50, display: "block" }} />
                    </div>}
                    <IconButton onClick={() => setOpen(false)} aria-label="Zbaliť bočný panel">
                        {theme.direction === 'rtl' ?
                            <IoChevronForwardCircleOutline /> :
                            <IoChevronBackCircleOutline />
                        }
                    </IconButton>
                </DrawerHeader>

                <Divider />

                <List>
                    {
                        Object.values(routes)
                            .filter((d) => {
                                if (d.hiddenInDrawer) return false;
                                if (!d.permissionType) return true;
                                return permissions.has(d.permissionType);
                            })
                            .map((definition, index) => <ListItem
                                setOpen={setOpen}
                                key={index + 1}
                                open={open}
                                {...definition}
                            ></ListItem>)
                    }
                </List>

                <Divider />

                <PopupsList open={open} />

                <Divider />

                {open && <div>
                    <Typography
                        whiteSpace="wrap"
                        textAlign="center"
                        variant="body2"
                        p={theme.spacing(1)}
                        style={{ fontSize: 12 }}
                    >
                        © {new Date().getFullYear()} Copyright {companyType === CompanyType.KOMUNALNA ? "KOMUNÁLNA" : "KOOPERATIVA"} Poisťovňa, a.s.
                        Vienna Insurance Group Všetky práva vyhradené. Web realizovala spoločnosť <br />
                        <a style={{ color: theme.palette.primary.main }} target="_blank" href="https://ecoding.sk/">
                            <strong>ECoding slovakia s.r.o.</strong>
                        </a>
                    </Typography>
                </div>}

                {assemblyVersion && <Typography
                    textAlign="center"
                    variant="caption"
                    title={`Verzia: ${assemblyVersion}`}
                    sx={{
                        display: "block",
                        px: 0.5,
                        pb: 1,
                        fontSize: open ? 12 : 9,
                        lineHeight: 1.3,
                        color: "text.secondary",
                        wordBreak: "break-word",
                    }}
                >
                    {open ? `Verzia: ${assemblyVersion}` : assemblyVersion}
                </Typography>}
            </Drawer>

            <Box
                width={getContentWidth()}
                component="main"
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    minWidth: 0,
                }}
            >
                <DrawerHeader />
                {children}
            </Box>
        </Box>
    );
}

const ListItem = ({
    open, icon, name, path, setOpen, pathDefinition
}: Route & { open: boolean, setOpen: (bool: boolean) => void }) => {
    const { hideAllPopups } = PopupProvider.usePopups();

    const theme = useTheme();

    const navigate = useNavigate();

    const active = useMatch(pathDefinition || path);

    const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const item = (
        <MuiListItem disablePadding sx={{ display: "block" }} onClick={() => {
            if (onlyMediumScreen) setOpen(false);
            hideAllPopups();
            navigate(path);
        }}>
            <ListItemButton
                sx={{
                    minHeight: 36,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        fontSize: 20,
                        color: active ? theme.palette.primary.main : theme.palette.grey[600],
                    }}
                >
                    {icon}
                </ListItemIcon>

                <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                    <Typography variant="subtitle2" component="h2" style={{
                        color: active ? theme.palette.primary.main : theme.palette.text.primary,
                    }}>{name}</Typography>
                </ListItemText>
            </ListItemButton>
        </MuiListItem>
    );

    if (!open) return <Tooltip placement="right" title={name}>
        {item}
    </Tooltip>

    return item;
}

const PopupsList = ({ open }: { open: boolean }) => {
    const { popups, openPopup, closePopup } = PopupProvider.usePopups();

    const theme = useTheme();

    const getSortedPopups = () => {
        const withoutReference = [...popups];

        withoutReference.sort((a, b) => {
            if (a.title > b.title) return 1;
            if (a.title < b.title) return -1;
            return 0;
        })

        return withoutReference;
    }

    return <Box flexGrow={1}>
        <List>
            {getSortedPopups().map((popup) => {
                const item =
                    <MuiListItem
                        key={popup.id}
                        disablePadding
                        onClick={() => openPopup(popup.id)}
                        sx={{
                            display: "block",
                            opacity: popup.hidden ? 0.5 : 1,
                        }}
                        secondaryAction={!open ? null :
                            <IconButton onClick={() => closePopup(popup.id)} edge="end" aria-label="delete">
                                <IoCloseOutline />
                            </IconButton>
                        }
                    >
                        <ListItemButton
                            sx={{
                                minHeight: 36,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                    fontSize: 20,
                                    color: theme.palette.grey[600],
                                }}
                            >
                                {popup.icon}
                            </ListItemIcon>

                            <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                                <TextOverflow text={popup.title} />
                            </ListItemText>
                        </ListItemButton>
                    </MuiListItem>

                if (!open) return <Tooltip key={`tooltip-${popup.id}`} placement="right" title={popup.title}>
                    {item}
                </Tooltip>

                return item;
            })}
        </List>
    </Box>
}
